/**
 * 远程控制信令服务模块，封装房间与候选交换接口。
 */

import { socket, waitForSocketConnection } from "@/services/remote/socket";
import { getCurrentPeer } from "@/services/remote/webrtc";
import { useConnectionStore } from "@/stores/remote";
import type {
	CheckDeviceResponse,
	CheckDeviceResult,
	RoomMessageEnvelope,
	SignalingError,
	SignalMessage,
} from "@/types/remote";

import { SIGNAL_SERVER_URL } from "../config";

/**
 * 保障仅返回纯对象值，遇到非对象或 null 则给 undefined
 */
const pickObject = <T extends Record<string, unknown>>(value: unknown) =>
	value && typeof value === "object" ? (value as T) : undefined;

/**
 * 将服务端返回的 code/message 规范化为前端可展示的英文提示。
 * - 该函数只处理“业务可恢复”的错误提示，避免 UI 混用中文。
 * @param {string | undefined} code 服务端/设备端错误码
 * @param {string | undefined} fallbackMessage 服务端透传的 message
 * @returns {string} 面向用户的英文提示
 */
const resolveCheckDeviceErrorMessage = (
	code?: string,
	fallbackMessage?: string,
) => {
	const normalizedCode = (code || "").toUpperCase();
	switch (normalizedCode) {
		case "ACCESS_CODE_INVALID":
			return "Incorrect access code";
		case "DEVICE_NOT_REGISTERED":
			return "Device is offline or not registered";
		case "DEVICE_DISCONNECTED":
			return "Device is offline";
		case "DEVICE_NO_RESPONSE":
			return "Device did not respond";
		case "DEVICE_BUSY":
			return "Device is busy";
		case "REMOTE_CONTROL_DISABLED":
			return "Remote control is disabled on target device";
		case "SCREEN_UNAVAILABLE":
			return "Screen capture is not available on target device";
		case "SCREEN_CHECK_FAILED":
			return "Failed to check target device screen availability";
		case "JOIN_ROOM_FAILED":
			return "Failed to prepare session on target device";
		default:
			// 兼容旧设备端仅返回 message（如 screen-unavailable）
			if (fallbackMessage === "screen-unavailable") {
				return "Screen capture is not available on target device";
			}
			if (fallbackMessage === "screen-check-failed") {
				return "Failed to check target device screen availability";
			}
			return fallbackMessage || "Failed to connect to target device";
	}
};

/**
 * 创建携带 code/status 的 Error，便于页面展示与埋点。
 * @param {object} params
 * @param {string} params.message 面向用户的错误提示
 * @param {string} [params.code] 错误码
 * @param {string} [params.status] 服务端 status
 * @returns {Error & {code?: string, status?: string}}
 */
const createSignalingError = ({
	message,
	code,
	status,
}: {
	message: string;
	code?: string;
	status?: string;
}): SignalingError => {
	const error = new Error(message) as SignalingError;
	if (code) error.code = code;
	if (status) error.status = status;
	return error;
};

export const getGuestToken = async (): Promise<Record<
	string,
	unknown
> | null> => {
	try {
		const url = `${SIGNAL_SERVER_URL}/api/generate-token`;

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error("Failed to get temp token");
		}

		const data = (await response.json()) as Record<string, unknown>;

		return data;
	} catch (error) {
		console.error("get temp token error:", error);
		return null;
	}
};

/**
 * 检查设备是否存在并返回会话信息，内部会等待 socket 就绪
 * @param {string} deviceId 设备 ID
 * @param {string} accessCode 访问码
 * @returns {Promise<{roomId: string, deviceInfo?: Record<string, unknown>, sessionInfo?: Record<string, unknown>, expiresAt?: string}>}
 * 返回房间 ID 以及可选的设备、会话信息
 */
export const checkDevice = async (
	deviceId: string,
	accessCode: string,
): Promise<CheckDeviceResult> => {
	await waitForSocketConnection();

	return new Promise((resolve, reject) => {
		if (!socket?.connected) {
			reject(new Error("Socket connection not available"));
			return;
		}

		socket.timeout(5000).emit(
			"check-device",
			{
				deviceId,
				accessCode,
			},
			(err, response: CheckDeviceResponse | undefined) => {
				console.log("[Signal] Device check response:", response);

				if (err) {
					console.error("[Signal] Device check timeout error:", err);
					reject(
						createSignalingError({
							status: "network_error",
							code: "CHECK_DEVICE_TIMEOUT",
							message: "Signal server request timed out",
						}),
					);
					return;
				}

				if (!response || typeof response !== "object") {
					reject(
						createSignalingError({
							status: "system_error",
							code: "INVALID_RESPONSE",
							message: "Invalid response format from server",
						}),
					);
					return;
				}

				const responseStatus = (response as { status?: string }).status;
				switch (response.status) {
					case "success":
						if (response.roomId && typeof response.roomId === "string") {
							console.log(
								"[Signal] Device check successful, room ID:",
								response.roomId,
							);
							resolve({
								roomId: response.roomId,
								deviceInfo: pickObject(response.deviceInfo),
								sessionInfo: pickObject(response.peerSessionInfo),
								expiresAt: response?.expiresAt ?? undefined,
							});
						} else {
							reject(
								createSignalingError({
									status: "system_error",
									code: "MISSING_ROOM_ID",
									message: "Server response missing room ID",
								}),
							);
						}
						break;

					// 兼容早期服务端/设备端返回
					case "error":
					case "business_error":
						reject(
							createSignalingError({
								status: "business_error",
								code: response.code || response.reason,
								message: resolveCheckDeviceErrorMessage(
									response.code || response.reason,
									response.message,
								),
							}),
						);
						break;

					case "network_error":
						reject(
							createSignalingError({
								status: "network_error",
								code: response.code || response.reason,
								message: resolveCheckDeviceErrorMessage(
									response.code || response.reason,
									response.message,
								),
							}),
						);
						break;

					case "system_error":
						reject(
							createSignalingError({
								status: "system_error",
								code: response.code || response.reason,
								message:
									response.message ||
									"System error occurred while processing request",
							}),
						);
						break;

					default:
						console.warn("[Signal] Unknown response status:", responseStatus);
						reject(
							createSignalingError({
								status: "system_error",
								code: "UNKNOWN_STATUS",
								message: `Unknown response status: ${responseStatus}`,
							}),
						);
				}
			},
		);
	});
};

/**
 * 加入房间
 * @param {string} roomId - 房间ID
 * @returns {Promise<void>}
 */
export const joinRoom = (roomId: string): Promise<void> => {
	return new Promise((resolve, reject) => {
		socket.timeout(5000).emit("join-room", roomId, (err, response) => {
			if (err) {
				console.error("[Signal] Join room timeout error:", err);
				reject(new Error(`Join room timeout: ${err.message || err}`));
			} else {
				if (response?.status === "success") {
					console.log("[Signal] Successfully joined room:", roomId);
					resolve();
				} else {
					reject(new Error(response?.error || "Failed to join room"));
				}
			}
		});
	});
};

export const sayGoodbye = (roomId: string) => {
	console.log("[Signal] Sending goodbye message to room:", roomId);

	const payload: RoomMessageEnvelope = {
		room: roomId,
		message: { type: "bye", content: null },
	};
	socket.emit("room-message", payload);
};

/**
 * 离开房间
 */
export const leaveRoom = () => {
	const roomId = useConnectionStore.getState().currentRoomId;
	if (roomId) {
		console.log("[Signal] Leaving room:", roomId);
		sayGoodbye(roomId);
		socket.emit("leave-room", roomId);
		useConnectionStore.getState().resetRoom();
	} else {
		console.warn("[Signal] No active room to leave");
	}
};

/**
 *  从 signal server 请求交换 sdp 信息
 * @date 2024 年 8 月 26 日
 * @returns Promise
 */
export const sendSdp = async (): Promise<void> => {
	try {
		const pc = getCurrentPeer();
		if (!pc) {
			throw new Error("WebRTC peer connection not available");
		}

		const offer = await pc.createOffer();
		console.log("[Signal] Created WebRTC offer:", offer);

		const sdp = offer.sdp;
		if (!sdp) {
			throw new Error("SDP content not available");
		}
		const message: SignalMessage = {
			type: "sdp",
			content: sdp,
		};

		const roomId = useConnectionStore.getState().currentRoomId;
		if (!roomId) {
			throw new Error("No active room for sending SDP");
		}

		const payload: RoomMessageEnvelope = {
			room: roomId,
			message,
		};
		socket.emit("room-message", payload);

		console.log("[Signal] SDP offer sent to room:", roomId);
	} catch (error) {
		console.error("[Signal] Failed to send SDP:", error);
		throw error;
	}
};

/**
 * 发送候选者信息
 * @param {string} candidate - 候选者信息
 */
export const sendCandidate = (
	candidate: RTCIceCandidateInit | RTCIceCandidate,
) => {
	console.log("[Signal] Sending ICE candidate:", candidate);

	const roomId = useConnectionStore.getState().currentRoomId;
	if (!roomId) {
		console.warn("[Signal] No active room for sending candidate");
		return;
	}

	const payload: RoomMessageEnvelope = {
		room: roomId,
		message: {
			type: "candidate",
			content:
				typeof (candidate as RTCIceCandidate).toJSON === "function"
					? (candidate as RTCIceCandidate).toJSON()
					: (candidate as RTCIceCandidateInit),
		},
	};
	socket.emit("room-message", payload);
};

/**
 * 发送心跳消息
 */
export const sendPing = () => {
	const roomId = useConnectionStore.getState().currentRoomId;
	if (!roomId) {
		console.warn("[Signal] No active room for sending ping");
		return;
	}

	console.log("[Signal] Sending ping to room:", roomId);
	const payload: RoomMessageEnvelope = {
		room: roomId,
		message: { type: "ping", content: null },
	};
	socket.emit("room-message", payload);
};

/**
 * 发送 ICE restart offer
 * @param {RTCSessionDescription} offer - ICE restart offer
 * @returns {Promise<void>}
 */
export const sendIceRestartOffer = async (offer: RTCSessionDescriptionInit) => {
	try {
		const pc = getCurrentPeer();
		if (!pc) {
			throw new Error("WebRTC peer connection not available");
		}

		console.log("[Signal] Created ICE restart offer:", offer);

		const sdp = offer.sdp;
		if (!sdp) {
			throw new Error("ICE restart offer SDP missing");
		}
		const message: SignalMessage = {
			type: "ice-restart-offer",
			content: sdp,
		};

		const roomId = useConnectionStore.getState().currentRoomId;
		if (!roomId) {
			throw new Error("No active room for sending ICE restart offer");
		}

		const payload: RoomMessageEnvelope = {
			room: roomId,
			message,
		};
		socket.emit("room-message", payload);

		console.log("[Signal] ICE restart offer sent to room:", roomId);
	} catch (error) {
		console.error("[Signal] Failed to send ICE restart offer:", error);
		throw error;
	}
};

/**
 * 发送 ICE restart answer
 * @param {RTCSessionDescription} answer - ICE restart answer
 * @returns {Promise<void>}
 */
export const sendIceRestartAnswer = async (
	answer: RTCSessionDescriptionInit,
) => {
	try {
		console.log("[Signal] Sending ICE restart answer:", answer);

		const sdp = answer.sdp;
		if (!sdp) {
			throw new Error("ICE restart answer SDP missing");
		}
		const message: SignalMessage = {
			type: "ice-restart-answer",
			content: sdp,
		};

		const roomId = useConnectionStore.getState().currentRoomId;
		if (!roomId) {
			throw new Error("No active room for sending ICE restart answer");
		}

		const payload: RoomMessageEnvelope = {
			room: roomId,
			message,
		};
		socket.emit("room-message", payload);

		console.log("[Signal] ICE restart answer sent to room:", roomId);
	} catch (error) {
		console.error("[Signal] Failed to send ICE restart answer:", error);
		throw error;
	}
};
